import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Divider, Fab, FormControlLabel, Tab, Tabs, Typography } from '@mui/material';
import useImage from 'services/Hooks/useImage';
import { useParams } from 'react-router';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { Add, AttachFile, Remove } from '@mui/icons-material';
import ImagesSlideView from 'Components/ImagesSlideView';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { v4 as uuidv4 } from 'uuid';
import { useActiveCart, usePostBrpClubCartTooling, usePostCartTooling } from 'services/Data/CartService';
import { useGetToolingPrice, useTooling } from 'services/Data/ToolingServices';
import FrequentlyBoughtTogether from '../Components/FrequentlyBoughtTogether';
import FrequentlyBoughtTogetherTypeEnum from 'enums/FrequentlyBoughtTogetherTypeEnum';
import { useSelector } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import InventoryItemDealerPortalTypeEnum from 'enums/InventoryItemDealerPortalTypeEnum';
import { IsClub, IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(theme => ({
  tabsList: {
    position: 'relative',
    minHeight: 10,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :hover': {
      color: theme.palette.secondary.main,

      '& >svg': {
        color: theme.palette.secondary.main,
      },
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main}`,
    },
  },
  tabItem: {
    maxWidth: 'none',
    minWidth: 10,
    minHeight: 10,
    paddingLeft: 20,
    paddingRight: 20,
    textTransform: 'none',
    color: theme.palette.common.black,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightRegular,
  },
  fileLink: {
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  hoverStyle: {
    '&:hover': {
      color: theme.palette.secondary.main,
      backgroundColor: `${theme.palette.secondary.main}08`,
    },
  },
}));

const Tooling = ({ toolingIdOverride, isQuickShop = false, onQuickShopSuccess } = {}) => {
  const classes = useStyles();

  const { t } = useTranslation();
  const { inventoryItemId: legacyInventoryItemId, dealerPortalMenuProductId } = useParams();
  const inventoryItemId = legacyInventoryItemId || dealerPortalMenuProductId;

  const { nameReference, labelReference } = useLanguageReference();

  const [price, setPrice] = useState(0);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const { getToolingImage } = useImage();

  const { data: tooling, isLoading: gettingTooling } = useTooling(inventoryItemId);
  const [configuration, setConfiguration] = useState({ quantity: 1, selectedAdditionalToolings: [] });
  const [currentTabValue, setCurrentTabValue] = useState(1);

  const { mutate: addCartTooling } = usePostCartTooling(accountReducer.isDemo);
  const { mutate: addBrpClubCartTooling } = usePostBrpClubCartTooling(false);
  const { mutateAsync: getToolingPrice } = useGetToolingPrice();

  const handleAddToCart = useCallback(
    tooling => {
      const cartToolingId = uuidv4();
      const cartTooling = {
        id: cartToolingId,
        dateCreated: new Date(),
        quantity: configuration.quantity,
        tooling_Id: tooling.id,
        subTotal: price,
        price: price / configuration.quantity,
        name: tooling.name,
        imgSrc: tooling?.images.find(i => i.dealerPortalPrimary)
          ? tooling?.images.find(i => i.dealerPortalPrimary)
          : tooling?.images[0],
        additionalToolings: configuration.selectedAdditionalToolings.map(atId => ({
          id: uuidv4(),
          dateCreated: new Date(),
          quantity: configuration.quantity,
          cartTooling_Id: cartToolingId,
          tooling_Id: atId,
        })),

        cart_Id: cart?.id,
      };
      if (IsClub) {
        addBrpClubCartTooling(cartTooling);
      } else {
        addCartTooling(cartTooling);
      }

      if (isQuickShop) {
        if (onQuickShopSuccess) {
          onQuickShopSuccess();
        }
      } else {
        setShowRedirectionModal(true);
      }
    },
    [
      addBrpClubCartTooling,
      addCartTooling,
      cart?.id,
      configuration.quantity,
      configuration.selectedAdditionalToolings,
      isQuickShop,
      onQuickShopSuccess,
      price,
    ],
  );

  useEffect(() => {
    if (!isQuickShop) {
      window.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [isQuickShop]);

  useEffect(() => {
    if (tooling?.id) {
      getToolingPrice({ ...configuration, toolingId: tooling.id }).then(({ data }) => {
        setPrice(data);
      });
    }
  }, [
    configuration,
    configuration.quantity,
    configuration.selectedAdditionalToolings,
    getToolingPrice,
    setConfiguration,
    tooling?.id,
  ]);

  const handleDownloadFile = useCallback((fileUrl, fileName) => {
    axios
      .get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Content-Disposition': 'attachment',
        },
      })
      .then(response => {
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(response.headers['content-disposition']);
        if (matches != null && matches[1]) {
          const originalFileName = matches[1].replace(/['"]/g, '');
          const extension = originalFileName.split('.')[1];

          link.setAttribute('download', `${fileName}.${extension}`);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }, []);

  const currentDealerPortalType = useMemo(
    () => InventoryItemDealerPortalTypeEnum.find(e => e.value === tooling?.dealerPortalTypeEnum),
    [tooling?.dealerPortalTypeEnum],
  );

  const currentParentDealerPortalType = useMemo(
    () => InventoryItemDealerPortalTypeEnum.find(e => e.value === currentDealerPortalType?.parentCategory),
    [currentDealerPortalType?.parentCategory],
  );

  if (!inventoryItemId || !tooling?.id) return null;

  return (
    <Box>
      {!isQuickShop && showRedirectionModal && (
        <CartRedirectionModal
          show={showRedirectionModal}
          onClose={() => setShowRedirectionModal(false)}
          redirectLink={'/dealer/cart'}
          title={t('itemAddedToCart')}
          message={t('itemAddedToCartText')}
        />
      )}

      <GridContainer>
        <GridItem xs={12} xl={8} lg={7} md={6}>
          <ImagesSlideView
            isLoading={gettingTooling}
            images={tooling?.images}
            getImageUrl={imgId => getToolingImage(tooling?.id, imgId)}
            enableMagnifier
          />
        </GridItem>
        <GridItem xs={12} xl={4} lg={5} md={6}>
          <CmtCard>
            <CmtCardContent>
              <Box display="flex" flexDirection={'column'} style={{ width: '100%', marginBottom: '1vh' }}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 33,
                  }}
                  isLoading={gettingTooling}
                  component={
                    <>
                      <Typography variant="h1">{tooling?.name}</Typography>
                      <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                        {`${currentParentDealerPortalType ? `${currentParentDealerPortalType[labelReference]} - ` : ''}${
                          currentDealerPortalType?.[labelReference]
                        }`}
                        non
                      </Typography>
                    </>
                  }
                />
                <Divider />
              </Box>

              <Box display="flex" flexDirection={'column'}>
                <SkeletonWrapper
                  skeletonProps={{
                    width: 210,
                    height: 60,
                  }}
                  isLoading={gettingTooling}
                  component={
                    <div>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}>
                        <Box>
                          <Typography variant="h2">{t('price')}</Typography>
                        </Box>
                        <Box>
                          <Typography fontSize={25} fontWeight="bold">
                            ${price.toFixed(2)} {tooling?.currencyName}
                          </Typography>
                        </Box>
                      </Box>
                      {tooling?.shortDescription && (
                        <Box>
                          <Divider sx={{ mt: 2, mb: 4 }} />
                          <Typography fontSize={14} justifyContent={'center'}>
                            {tooling?.shortDescription}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  }
                />

                {tooling?.additionalToolings?.length > 0 && (
                  <>
                    <Divider sx={{ mt: 2, mb: 4 }} />
                    <Typography variant="h6" sx={{ pb: 2 }}>
                      {t('choose_additional_tooling')}
                    </Typography>
                    <SkeletonWrapper
                      skeletonProps={{
                        width: 210,
                        height: 36,
                      }}
                      isLoading={gettingTooling}
                      component={tooling?.additionalToolings?.map(addTooling => (
                        <FormControlLabel
                          className={classes.hoverStyle}
                          control={
                            <Checkbox
                              checked={configuration.selectedAdditionalToolings?.some(at => at === addTooling.id)}
                              onChange={(event, value) => {
                                if (value && configuration.selectedAdditionalToolings?.every(sa => sa !== addTooling.id)) {
                                  setConfiguration({
                                    ...configuration,
                                    selectedAdditionalToolings: [...configuration.selectedAdditionalToolings, addTooling.id],
                                  });
                                } else if (!value) {
                                  setConfiguration({
                                    ...configuration,
                                    selectedAdditionalToolings: [
                                      ...(configuration.selectedAdditionalToolings?.filter(at => at !== addTooling.id) ||
                                        []),
                                    ],
                                  });
                                }
                              }}
                            />
                          }
                          label={addTooling.name}
                        />
                      ))}
                    />
                  </>
                )}

                <Divider sx={{ mt: 2, mb: 4 }} />

                <Box
                  sx={{
                    pt: 0,
                    display: 'flex',
                    flexDirection: isQuickShop ? 'column' : 'row',
                    justifyContent: isQuickShop ? 'flex-start' : 'space-between',
                    alignItems: isQuickShop ? 'stretch' : 'center',
                    gap: isQuickShop ? 2 : 0,
                  }}>
                  <Box
                    fontSize={20}
                    sx={{
                      width: isQuickShop ? '100%' : '15vh',
                      mt: isQuickShop ? 0 : 4,
                      mb: isQuickShop ? 0 : 5,
                    }}
                    display="flex"
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Fab
                      size="small"
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                      }}
                      disabled={configuration.quantity <= 0}
                      onClick={() =>
                        setConfiguration({
                          ...configuration,
                          quantity: configuration.quantity - 1,
                        })
                      }>
                      <Remove />
                    </Fab>
                    <Typography>{configuration.quantity}</Typography>
                    <Fab
                      size="small"
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                      }}
                      onClick={() =>
                        setConfiguration({
                          ...configuration,
                          quantity: configuration.quantity + 1,
                        })
                      }>
                      <Add />
                    </Fab>
                  </Box>
                  <Box sx={{ width: isQuickShop ? '100%' : 'auto' }}>
                    <Button
                      variant={'contained'}
                      color={'secondary'}
                      sx={{
                        borderRadius: 0,
                        color: '#000',
                        mt: isQuickShop ? 1 : 0,
                        '&:hover': {
                          backgroundColor: '#EFBA29',
                          boxShadow: 'none',
                        },
                      }}
                      fullWidth={isQuickShop}
                      disabled={
                        tooling?.additionalToolings?.length > 0 && configuration.selectedAdditionalToolings?.length === 0
                      }
                      onClick={() => handleAddToCart(tooling)}>
                      <Typography
                        sx={{
                          color: 'black !important',
                          fontSize: 22,
                          fontWeight: 800,
                          paddingRight: 0,
                          paddingLeft: 0,
                          paddingTop: 2,
                          paddingBottom: 2,
                          fontFamily: 'Trade Gothic Next LT Pro',
                          letterSpacing: '-0.02em',
                          mb: 1,
                        }}>
                        {t('addToCart')}
                      </Typography>
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CmtCardContent>
          </CmtCard>
          <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
            <CmtCardContent>
              <Box>
                <TabContext value={currentTabValue}>
                  <Tabs
                    className={classes.tabsList}
                    value={currentTabValue}
                    onChange={(_, value) => {
                      setCurrentTabValue(value);
                    }}
                    aria-label="profile tabs"
                    variant="scrollable"
                    allowScrollButtonsMobile>
                    <Tab className={classes.tabItem} value={1} label={t('overview')} />
                    <Tab className={classes.tabItem} value={2} label={t('installationFiles')} />
                  </Tabs>

                  <TabPanel value={1}>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingTooling}
                      component={
                        <Typography>
                          <span
                            dangerouslySetInnerHTML={{
                              __html: tooling?.description,
                            }}
                          />
                        </Typography>
                      }
                    />
                  </TabPanel>
                  <TabPanel value={2}>
                    <Typography>{tooling?.userGuideFiles?.length > 0 ? t('filesInfo') : t('noFiles')}</Typography>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingTooling}
                      component={
                        tooling && (
                          <Box sx={{ ml: 5 }}>
                            <ul>
                              {tooling.userGuideFiles?.map(ugf => (
                                <li key={ugf.id}>
                                  <span
                                    onClick={() =>
                                      handleDownloadFile(getToolingImage(tooling.id, ugf.id), ugf[nameReference])
                                    }
                                    className={classes.fileLink}>
                                    <AttachFile color="secondary" fontSize="small" sx={{ marginBottom: '-0.3rem' }} />{' '}
                                    {ugf[nameReference]}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )
                      }
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </CmtCardContent>
          </CmtCard>
        </GridItem>

        <GridItem xs={12}>
          <FrequentlyBoughtTogether
            entityId={tooling.id}
            frequentlyBoughtTogetherType={FrequentlyBoughtTogetherTypeEnum.Tooling}
          />
        </GridItem>
      </GridContainer>
    </Box>
  );
};

export default Tooling;
